import React from 'react'
import { Icon } from 'semantic-ui-react'
import classnames from 'classnames'
import { Button, Card, Stack, Typography } from '@prometheus/components'
import { ReadyRequirement } from '../../readyGate'
import './ReadyRequirementOverlay.scss'

export interface ReadyRequirementOverlayProps {
  requirement: ReadyRequirement
  /** A call is in flight — every control here goes inert. */
  busy?: boolean
  /** Travel to a named destination. */
  onChoose: (planet: string) => void
  /** Let the game decide — the same thing a second Ready tap does. */
  onAuto: () => void
  onDismiss: () => void
  /** Which edge the panel hangs from. The header button sits at the right. */
  align?: 'left' | 'right'
  className?: string
}

/**
 * The options panel that drops out from under the Ready button once the first
 * tap has revealed it: what the game is still waiting on, the choices that
 * answer it, and a standing reminder that tapping Ready again hands the
 * decision over. Purely presentational — `useReadyGate` owns the state.
 */
export const ReadyRequirementOverlay = ({
  requirement,
  busy,
  onChoose,
  onAuto,
  onDismiss,
  align = 'right',
  className,
}: ReadyRequirementOverlayProps) => (
  <Card
    className={classnames('pg-ready-req', `pg-ready-req--${align}`, className)}
    padding="none"
    borderColor="warning"
    role="dialog"
    aria-label={requirement.title}
  >
    <Stack space="sm" className="pg-ready-req__body">
      <Stack direction="row" className="pg-ready-req__head">
        <Typography variant="h4" retro color="warning">{requirement.title}</Typography>
        <button
          type="button"
          className="pg-ready-req__close"
          onClick={onDismiss}
          aria-label="Close"
        >
          <Icon name="close" />
        </button>
      </Stack>
      <Typography variant="body2">{requirement.hint}</Typography>

      {!!requirement.choices.length && (
        <div className="pg-ready-req__choices">
          {requirement.choices.map(choice => (
            <Button
              key={choice.key}
              className="pg-ready-req__choice"
              size="sm"
              variant="primary"
              outline
              disabled={busy}
              onClick={() => onChoose(choice.key)}
            >
              <Icon name="rocket" />
              <span className="pg-ready-req__choice-name">{choice.name}</span>
              {choice.orbitName !== choice.name && (
                <span className="pg-ready-req__choice-orbit">{choice.orbitName}</span>
              )}
            </Button>
          ))}
        </div>
      )}

      {requirement.autoLabel && (
        <Stack space="xs" className="pg-ready-req__auto">
          <Button
            size="sm"
            variant="warning"
            fullWidth
            disabled={busy}
            onClick={onAuto}
          >
            <Icon name={requirement.kind === 'trade' ? 'exchange' : 'random'} />
            {requirement.kind === 'trade'
              ? (requirement.autoLabel === 'skip the trade' ? 'Skip Trade' : 'Commit Bids')
              : 'Pick For Me'}
          </Button>
          <Typography variant="body2" color="secondary" className="pg-ready-req__auto-hint">
            Tap Ready again to {requirement.autoLabel}.
          </Typography>
        </Stack>
      )}
    </Stack>
  </Card>
)

export default ReadyRequirementOverlay
